import * as React from 'react'
import GuardedRoute from 'src/utils-components/guarded/Route'
import GuardedSubMenu from 'src/utils-components/guarded/SubMenu'
import logo from 'src/logo.png'
import MenuItem from 'src/utils-components/guarded/MenuItem'
import { Button, Col, Dropdown, Icon, Layout, Menu, Row } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { Link, Redirect, Route, Switch, withRouter } from 'react-router-dom'
import { Location } from 'history'
import { loginActions } from 'src/users-components/login/login.actions'
import { getOrElse, isSome } from 'fp-ts/lib/Option'
import { State } from 'src/app.store'
import { Statuses } from '@yobora/models/rights'
import { style } from 'typestyle'
import { UserIdentity } from 'src/users-components/login/login.reducer'
import 'src/App.scss'
import { pipe } from 'fp-ts/lib/pipeable'

const { Header, Sider, Content } = Layout

interface AppProps {
  location: Location
}

const AsyncHome = React.lazy(() => import('src/home/Home'))
const AsyncGiftCredits = React.lazy(() => import('src/credits-components/gift-credits/GiftCredits'))
const AsyncCreateCompany = React.lazy(() => import('src/companies-components/create-company/CreateCompany'))
const AsyncCreateUser = React.lazy(() => import('src/users-components/create-user/CreateUser'))
const AsyncUpdateCompanies = React.lazy(() => import('src/companies-components/update-company/UpdateCompany'))
const AsyncUpdateMyProfile = React.lazy(() => import('src/users-components/me/Me'))
const AsyncUpdateMyCompany = React.lazy(() => import('src/companies-components/update-my-company/UpdateMyCompany'))
const AsyncLogin = React.lazy(() => import('src/users-components/login/Login'))
const AsyncUpdateTest = React.lazy(() => import('src/tests-components/update-test/UpdateTest'))
const AsyncLostPassword: React.ComponentType<any> = React.lazy(() => import('src/users-components/lost-password/LostPassword'))
const AsyncInviteUserTest = React.lazy(() => import('src/tests-components/invite-user/InviteUserTest'))
const AsyncFinalizeRegister: React.ComponentType<any> = React.lazy(() =>
  import('src/users-components/finalize-register/FinalizeRegister')
)
const AsyncTestPage: React.ComponentType<any> = React.lazy(() => import('./tests-components/test-page/TestPage'))

const layoutStyle = style({ backgroundColor: '#e4e5e6', height: '100%' })
const headerStyle = style({
  backgroundColor: 'white',
  borderBottom: '1px solid #a4b7c1',
  padding: 0,
  height: '55px',
  lineHeight: '55px'
})
const logoStyle = style({ height: '58%' })
const menuStyle = style({
  height: '100%'
})

const userMenu = (logout: () => void) => (
  <Menu
    onClick={param => {
      if (param.key === 'logout') {
        logout()
      }
    }}
  >
    <Menu.Item key="user-account">
      <Link to="/user/me">
        <Icon type="user" /> Mon compte
      </Link>
    </Menu.Item>
    <MenuItem key="user-company" rights={[Statuses.admin, Statuses.yobora]}>
      <Link to="/company/update">
        <Icon type="shop" /> Mon entreprise
      </Link>
    </MenuItem>
    <Menu.Divider />
    <Menu.Item key="logout">
      <Icon type="logout" /> Me déconnecter
    </Menu.Item>
  </Menu>
)

const App: React.FC<AppProps> = props => {
  const authenticated = useSelector((s: State) => isSome(s.login.identity))
  const identity = useSelector((s: State) => s.login.identity)
  const dispatch = useDispatch()
  const doLogout = () => dispatch(loginActions.Logout())

  return (
    <Layout className={layoutStyle}>
      <Header className={headerStyle}>
        <img src={logo} className={logoStyle}  alt="Company logo"/>
        {authenticated ? (
          <Dropdown overlay={userMenu(doLogout)} trigger={['click', 'hover']}>
            <Button shape="circle" size="large" type="primary" style={{ float: 'right', top: '7px', right: '10px' }}>
              {
                pipe(
                  identity,
                  getOrElse<UserIdentity>(() => ({ companyId: '', userName: '', rights: [], token: '', initials: '' }))
                ).initials
              }
            </Button>
          </Dropdown>
        ) : (
          <div />
        )}
      </Header>
      <Layout>
        {authenticated ? (
          <Sider width={200} breakpoint="sm" collapsible>
            <Menu mode="inline" theme="dark" defaultSelectedKeys={['home']} className={menuStyle}>
              <Menu.Item key="home">
                <Link to="/home">
                  <Icon type="home" />
                  <span>Accueil</span>
                </Link>
              </Menu.Item>
              <GuardedSubMenu
                key="users"
                rights={[Statuses.yobora]}
                title={
                  <span>
                    <Icon type="team" />
                    <span>Utilisateurs</span>
                  </span>
                }
              >
                <Menu.Item key="users-add">
                  <Link to="/users/add">
                    <Icon type="plus-circle-o" />
                    <span>Ajouter</span>
                  </Link>
                </Menu.Item>
                <Menu.Item key="users-update">
                  <Icon type="edit" />
                  <span>Modifier</span>
                </Menu.Item>
              </GuardedSubMenu>
              <GuardedSubMenu
                rights={[Statuses.yobora]}
                key="companies"
                title={
                  <span>
                    <Icon type="shop" />
                    <span>Entreprises</span>
                  </span>
                }
              >
                <Menu.Item key="companies-add">
                  <Link to="/companies/add">
                    <Icon type="plus-circle-o" />
                    <span>Ajouter</span>
                  </Link>
                </Menu.Item>
                <Menu.Item key="companies-update">
                  <Link to="/companies/update">
                    <Icon type="edit" />
                    <span>Modifier</span>
                  </Link>
                </Menu.Item>
              </GuardedSubMenu>
              <GuardedSubMenu
                key="tests"
                rights={[Statuses.yobora, Statuses.habilitated_coach]}
                title={
                  <span>
                    <Icon type="schedule" />
                    <span>Tests</span>
                  </span>
                }
              >
                <MenuItem key="tests-gift" rights={[Statuses.yobora]}>
                  <Link to="/tests/gift">
                    <Icon type="gift" />
                    <span>Offrir</span>
                  </Link>
                </MenuItem>
                <Menu.Item key="tests-invite">
                  <Link to="/tests/invite">
                    <Icon type="rocket" />
                    <span>Inviter</span>
                  </Link>
                </Menu.Item>
                <MenuItem key="tests-update" rights={[Statuses.yobora]}>
                  <Link to="/tests/update">
                    <Icon type="edit" />
                    <span>Modifier</span>
                  </Link>
                </MenuItem>
              </GuardedSubMenu>
            </Menu>
          </Sider>
        ) : (
          <div />
        )}
        <Content style={{ marginTop: '10px' }}>
          {authenticated ? (
            <React.Suspense fallback={<Icon type="loading" style={{ fontSize: 24 }} spin />}>
              <Switch>
                <Route exact path="/home" component={AsyncHome} />
                <Route exact path="/user/me" component={AsyncUpdateMyProfile} />
                <Route exact path="/company/update" component={AsyncUpdateMyCompany} />
                <GuardedRoute rights={[Statuses.yobora]} exact path="/companies/add" component={AsyncCreateCompany} />
                <GuardedRoute rights={[Statuses.yobora]} exact path="/tests/gift" component={AsyncGiftCredits} />
                <GuardedRoute
                  rights={[Statuses.yobora]}
                  exact
                  path="/companies/update"
                  component={AsyncUpdateCompanies}
                />
                <GuardedRoute
                  rights={[Statuses.yobora, Statuses.admin]}
                  exact
                  path="/company/update"
                  component={AsyncUpdateMyCompany}
                />
                <GuardedRoute rights={[Statuses.yobora]} exact path="/users/add" component={AsyncCreateUser} />
                <Route exact path="/tests/invite" component={AsyncInviteUserTest} />
                <GuardedRoute rights={[Statuses.yobora]} exact path="/tests/update" component={AsyncUpdateTest} />
                <Route exact path="/test" component={() => <AsyncTestPage location={props.location} />} />
                <Route exact path="/forbidden" component={() => <div>Interdit !</div>} />
                <Route exact path="/bad" component={() => <div>URL demandée invalide !</div>} />
                <Redirect to="/home" />
              </Switch>
            </React.Suspense>
          ) : (
            <React.Suspense fallback={<Icon type="loading" style={{ fontSize: 24 }} spin />}>
              <Switch>
                  <Route exact path="/lostPassword" component={() => <AsyncLostPassword location={props.location} />} />
                  <Route exact path="/test" component={() => <AsyncTestPage location={props.location} />} />
                <Route exact path="/finalize" component={() => <AsyncFinalizeRegister location={props.location} />} />
                <Route exact path="/login" component={LoginComp} />
                <Redirect to="/login" />
              </Switch>
            </React.Suspense>
          )}
        </Content>
      </Layout>
    </Layout>
  )
}

const LoginComp = () => (
  <Row type="flex" justify="center" align="middle" style={{ height: '100%' }}>
    <Col xs={24} sm={18} md={12}>
      <AsyncLogin />
    </Col>
  </Row>
)

export default withRouter(props => <App location={props.location} />)



// WEBPACK FOOTER //
// ./src/App.tsx